/**
 * Realm helpers — resolve realmId from parent container.
 *
 * INTERNAL — not exported from operations/index.ts.
 */

import { getDb } from "../runtime.js"

/** Segments starting with # are virtual containers (no DB object). */
export function isVirtualContainer(segment: string): boolean {
  return segment.startsWith("#")
}

/**
 * Determine the correct realmId for an object being placed at `parentPath`.
 *
 * Resolution order:
 * 1. Walk path segments from most specific to root
 * 2. Skip virtual containers (#inbox, #anyday, etc.)
 * 3. Look up real containers — return their realmId if shared
 * 4. If nothing found → return currentUserId (private realm)
 *
 * NEVER returns undefined or "". Always returns a valid realmId.
 */
export async function resolveContainerRealmId(
  parentPath: string,
): Promise<string> {
  const db = getDb()
  const privateRealm = db.cloud.currentUserId

  if (!parentPath || parentPath === "") return privateRealm

  const segments = parentPath.split("/")

  for (let i = segments.length - 1; i >= 1; i--) {
    const id = segments[i]!
    if (isVirtualContainer(id)) continue

    const obj = await db.totodo.get(id)
    if (obj?.realmId) return obj.realmId
  }

  return privateRealm
}

/**
 * @deprecated Use `resolveContainerRealmId` instead.
 */
export async function resolveRealmId(
  parentPath: string,
): Promise<string | undefined> {
  if (!parentPath || parentPath === "") return undefined

  const db = getDb()
  const segments = parentPath.split("/")

  for (let i = segments.length - 1; i >= 1; i--) {
    const id = segments[i]!
    if (isVirtualContainer(id)) continue

    const obj = await db.totodo.get(id)
    if (obj?.realmId) return obj.realmId
  }

  return undefined
}
